import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import "font-awesome/css/font-awesome.min.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from 'react-redux';
import App from "./App";
import './i18n';
import "./index.css";
import { store } from './store';
import TagManager from "react-gtm-module";
import { initAnalyticsBridge, initPageTimeTracking } from './helper/analyticsService';

// 🔐 Get nonce from meta (must be injected by backend)
const nonce =
  document.querySelector('meta[property="csp-nonce"]')?.getAttribute('content') || "";

// ==========================
// 🔥 GLOBAL NONCE ENFORCEMENT (CRITICAL)
// ==========================

// Patch createElement (covers most dynamic injections)
const originalCreateElement = document.createElement;
document.createElement = function (tagName, options) {
  const el = originalCreateElement.call(document, tagName, options);

  if (tagName === "style" || tagName === "script") {
    if (nonce) {
      el.setAttribute("nonce", nonce);
    }
  }

  return el;
};

// Fallback: MutationObserver (catches edge cases like GTM, recaptcha, etc.)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (
        (node.tagName === "STYLE" || node.tagName === "SCRIPT") &&
        !node.nonce
      ) {
        node.setAttribute("nonce", nonce);
      }
    });
  });
});

observer.observe(document.head, { childList: true });

// ==========================
// 🎨 Emotion Cache (MUI fix)
// ==========================
const emotionCache = createCache({
  key: 'csp-nonce',
  nonce: nonce,
  prepend: true,
});

// ==========================
// 📊 Analytics / GTM
// ==========================
const tagManagerArgs = {
  gtmId: import.meta.env.VITE_GTM_ID,
};

// IMPORTANT: initialize AFTER nonce patch
TagManager.initialize(tagManagerArgs);

initAnalyticsBridge();
initPageTimeTracking();

// ==========================
// ⚛️ React App Render
// ==========================
const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <App />
      </CacheProvider>
    </Provider>
  </React.StrictMode>
);

// ==========================
// 🧩 Micro frontend support
// ==========================
window.rendercreatereactapp = (containerId, history) => {
  ReactDOM.createRoot(
    document.getElementById(containerId)
  ).render(
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <App history={history} />
      </CacheProvider>
    </Provider>
  );
};

window.unmountcreatereactapp = (containerId) => {
  const el = document.getElementById(containerId);
  if (el) {
    ReactDOM.createRoot(el).unmount();
  }
};